import {
	AdjustmentGroups,
	AdjustmentGroup,
	DefaultExcludedAdjustmentKeys,
	isAdjustmentGroup,
	isAdjustmentKey,
	getDefaultAdjustments,
} from "@/core/adjustments"
import { Dialog } from "../ui/Dialog"
import { useTranslation } from "react-i18next"
import { AdjustmentKey } from "@/core/types"
import { useRef, useState } from "react"
import { Button } from "../ui/Button"
import { adjustmentsClipBoardAtom, copyCurrentAdjustments } from "@/store/store-history"
import { popDialog } from "@/store/dialogs-state"
import { useAtomValue } from "jotai"
import { Checkbox, CheckboxItem } from "../ui/Checkbox"
import { createPresetFromCurrentAdjustments } from "@/store/store-presets"
import { Icon } from "../icons/Icon"
import { ControlGroup } from "../ControlGroup"
import { expandPanel, expandedPanelsAtom } from "@/store/ui-views-state"
import { useShortcuts } from "@/utils/useShortcuts"

export const CopyAdjustmentsDialog = () => {
	const { t } = useTranslation("components")
	const copiedAdjustments = useAtomValue(adjustmentsClipBoardAtom)

	const getDefaultAdjustmentKeys = () => {
		if (copiedAdjustments) {
			return new Set(Object.keys(copiedAdjustments) as AdjustmentKey[])
		}
		const defaultAdjustments = getDefaultAdjustments()
		const all = Object.keys(defaultAdjustments) as AdjustmentKey[]
		return new Set(
			all.filter((key) => {
				return !DefaultExcludedAdjustmentKeys.includes(key)
			}),
		)
	}

	return (
		<Dialog trackingViewName="copy_adjustments_dialog" disableClose>
			<div className="flex w-[500px] flex-col items-start justify-start space-y-4">
				<div className="text-start text-md font-bold">
					{t("selectAdjustmentsDialog.copySettings")}
				</div>
				<AdjustmentsSelection
					defaultSelection={getDefaultAdjustmentKeys()}
					confirmButtonTitle={t("selectAdjustmentsDialog.confirmCopy")}
					confirmSelection={(keys) => {
						copyCurrentAdjustments(keys)
						popDialog()
					}}
				/>
			</div>
		</Dialog>
	)
}

export const CreatePresetDialog = () => {
	const { t } = useTranslation("components")
	const [name, setName] = useState(t("selectAdjustmentsDialog.untitledPreset"))
	const inputRef = useRef<HTMLInputElement>(null)

	const getDefaultAdjustmentKeys = () => {
		const defaultAdjustments = getDefaultAdjustments()
		const all = Object.keys(defaultAdjustments) as AdjustmentKey[]
		return new Set(
			all.filter((key) => {
				return !DefaultExcludedAdjustmentKeys.includes(key)
			}),
		)
	}

	const createPreset = (keys: Set<AdjustmentKey>) => {
		createPresetFromCurrentAdjustments(keys, name)
	}

	return (
		<Dialog
			trackingViewName="create_preset_dialog"
			disableClose
			onOpenAutoFocus={(e) => {
				e.preventDefault()
				inputRef.current?.focus()
				inputRef.current?.select()
			}}
		>
			<div className="flex w-[500px] flex-col items-start justify-start space-y-6">
				<div className="text-start text-md font-bold">
					{t("selectAdjustmentsDialog.createPreset")}
				</div>
				<input
					ref={inputRef}
					className="w-full rounded p-1.5 text-sm"
					value={name}
					onChange={(e) => {
						setName(e.target.value)
					}}
				/>
				<AdjustmentsSelection
					defaultSelection={getDefaultAdjustmentKeys()}
					confirmButtonTitle={t("selectAdjustmentsDialog.confirmCreate")}
					confirmSelection={(keys) => {
						createPreset(keys)
						popDialog()
					}}
					hideSelection={["transform", "cropGroup"]} //Create preset should hide transform and crop selection since they cannot be applied
				/>
			</div>
		</Dialog>
	)
}

export function AdjustmentsSelection(props: {
	defaultSelection: Set<AdjustmentKey>
	confirmButtonTitle: string
	confirmSelection: (keys: Set<AdjustmentKey>) => void
	hideSelection?: (AdjustmentKey | AdjustmentGroup)[]
}) {
	const { t } = useTranslation("components")
	const [selectedAdjustmentKeys, setSelectedAdjustmentKeys] = useState<Set<AdjustmentKey>>(
		props.defaultSelection,
	)
	const expandedPanels = useAtomValue(expandedPanelsAtom)

	useShortcuts({
		keys: ["enter"],
		action: () => {
			if (selectedAdjustmentKeys.size > 0) {
				props.confirmSelection(selectedAdjustmentKeys)
			}
		},
		trackingLabel: "confirm_adjustments_selection",
		scope: "dialog",
		disabled: selectedAdjustmentKeys.size === 0,
		preventDefault: true,
	})

	const toggleItem = (id: AdjustmentGroup | AdjustmentKey, isChecked: boolean) => {
		if (isAdjustmentGroup(id)) {
			for (const key of AdjustmentGroups[id as AdjustmentGroup]) {
				toggleItem(key, isChecked)
			}
		} else if (isAdjustmentKey(id)) {
			if (isChecked) {
				selectedAdjustmentKeys.add(id as AdjustmentKey)
				setSelectedAdjustmentKeys(new Set(selectedAdjustmentKeys))
			} else {
				selectedAdjustmentKeys.delete(id as AdjustmentKey)
				setSelectedAdjustmentKeys(new Set(selectedAdjustmentKeys))
			}
		}
	}

	const getItemState = (id: AdjustmentGroup | AdjustmentKey) => {
		if (isAdjustmentGroup(id)) {
			let checkedCount = 0
			for (const key of AdjustmentGroups[id as AdjustmentGroup]) {
				if (getItemState(key) === true) {
					checkedCount++
				}
			}
			return checkedCount === 0
				? false
				: checkedCount === AdjustmentGroups[id as AdjustmentGroup].length
					? true
					: "indeterminate"
		} else if (isAdjustmentKey(id)) {
			return selectedAdjustmentKeys.has(id as AdjustmentKey)
		} else {
			return false
		}
	}

	const displayName = (id: string) => {
		return t(`selectAdjustmentsDialog.adj_${id}`)
	}

	const checkAll = () => {
		for (const key of Object.keys(AdjustmentGroups)) {
			toggleItem(key as AdjustmentGroup | AdjustmentKey, true)
		}
	}

	const checkNone = () => {
		setSelectedAdjustmentKeys(new Set())
	}

	const renderItem = (id: AdjustmentGroup | AdjustmentKey, indent?: boolean) => {
		if (props.hideSelection?.includes(id)) {
			return null
		} else {
			return (
				<CheckboxItem
					trackingLabel={`select_adjustment_${id}`}
					id={id}
					checkedState={getItemState(id)}
					content={displayName(id)}
					onCheckChanged={toggleItem}
					indent={indent}
				/>
			)
		}
	}

	const renderGroup = (group: AdjustmentGroup) => {
		if (props.hideSelection?.includes(group)) {
			return null
		}
		const groupLabel = displayName(group)
		const groupState = getItemState(group)
		const expandKey = `select_adjustments_${group}`
		const isExpanded = !!expandedPanels[expandKey]

		return (
			<ControlGroup
				name={groupLabel}
				expandKey={expandKey}
				isExpanded={false}
				compact
				fullSizeContent
				hideSeparator
				className="px-0"
				customExpandView={() => (
					<div className="flex items-center" onClick={(e) => e.stopPropagation()}>
						<Checkbox
							trackingLabel={`select_adjustment_${group}`}
							checked={groupState !== false}
							state={groupState}
							onClick={(e) => e.stopPropagation()}
							onCheckedChange={(checked) => {
								if (checked === "indeterminate") {
									toggleItem(group, true)
									return
								}
								toggleItem(group, checked === true)
							}}
						/>
					</div>
				)}
				customNameView={
					<div
						className="ml-2 flex min-w-0 items-center text-sm"
						onClick={(e) => {
							e.stopPropagation()
							if (groupState === "indeterminate") {
								toggleItem(group, true)
							} else {
								toggleItem(group, !groupState)
							}
						}}
					>
						{groupLabel}
						<Icon
							name="expand_right"
							className="transition-transform"
							onClick={(e) => {
								e.stopPropagation()
								expandPanel(expandKey, !isExpanded)
							}}
							style={{
								transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
							}}
							size={20}
						/>
					</div>
				}
			>
				<div className="ml-2 flex flex-col space-y-1">
					{AdjustmentGroups[group].map((key) => {
						return renderItem(key, true)
					})}
				</div>
			</ControlGroup>
		)
	}

	return (
		<div className="flex w-full flex-col items-start justify-start space-y-6">
			<div className="flex w-full flex-row justify-between space-x-3">
				<div className="flex w-1/2 flex-col items-start justify-start space-y-3">
					{renderItem("profile")}
					{renderItem("lut")}
					{renderGroup("basic")}
					{renderGroup("curves")}
				</div>

				<div className="flex w-1/2 flex-col items-start justify-start space-y-3">
					{renderItem("HSL")}
					{renderItem("pointColors")}
					{renderItem("colorGrading")}
					{renderGroup("detail")}
					{renderItem("transform")}
					{renderItem("cropGroup")}
					{renderGroup("effects")}
					{renderItem("lensCorrection")}
					{renderItem("calibration")}
					{renderItem("masking")}
					{renderItem("healing")}
				</div>
			</div>
			<div className="flex w-full flex-row justify-between">
				<div className="flex flex-row space-x-2">
					<Button
						trackingLabel="check_all"
						aria-label={t("selectAdjustmentsDialog.ariaCheckAll")}
						size="sm"
						onClick={checkAll}
					>
						{t("selectAdjustmentsDialog.checkAll")}
					</Button>
					<Button
						trackingLabel="check_none"
						aria-label={t("selectAdjustmentsDialog.ariaCheckNone")}
						size="sm"
						onClick={checkNone}
					>
						{t("selectAdjustmentsDialog.checkNone")}
					</Button>
				</div>
				<div className="flex flex-row space-x-2">
					<Button trackingLabel="cancel" size="sm" onClick={() => popDialog()}>
						{t("common:action.cancel")}
					</Button>
					<Button
						trackingLabel="confirm"
						variant="primary"
						size="sm"
						onClick={() => props.confirmSelection(selectedAdjustmentKeys)}
						disabled={selectedAdjustmentKeys.size === 0}
					>
						{props.confirmButtonTitle}
					</Button>
				</div>
			</div>
		</div>
	)
}
